import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { API } from '../Root';
import { setInitialDashboardSettings, setUpdateBackend, setIsLoading } from '../pages/dashboard/store';

/**
 * Hook that manages syncing dashboard state with backend.
 *
 * On component mount:
 * - Fetches saved dashboard settings from backend using get_dashboard_settings endpoint
 * - Updates Redux store with saved user preferences using setInitialDashboardSettings action
 *
 * On state updates:
 * - Watches the updateBackend flag which is set to true when any tracked values change
 * - Automatically posts the dashboard store object (excluding summaryData, isCustomModalOpen, isLoading)
 *   to backend via user/update/dashboard_settings endpoint
 * - Sets updateBackend to false after successful post
 */
export function useDashboardUpdateHook() {
    const dispatch = useDispatch();
    const isMountingRef = useRef(false);

    // Subscribe to Redux state - track the fields we need to persist
    const selectedEquipment = useSelector(state => {
        return state.dashboard.get('selectedEquipment', {});
    });

    const dateRange = useSelector(state => {
        return state.dashboard.get('dateRange', { dates: ['', ''], dropdownValue: 'mtd' });
    });

    const tabSelected = useSelector(state => {
        return state.dashboard.get('tabSelected', 'usage');
    });

    const updateBackend = useSelector(state => {
        return state.dashboard.get('updateBackend', false);
    });


    // Effect 1: Fetch initial dashboard settings on mount
    useEffect(() => {
        const fetchDashboardSettings = async () => {
            dispatch(setIsLoading(true));
            try {
                const apiResponse = await API.call({
                    endpoint: API.endpoints['getDashboardSettings'],
                    method: 'get',
                });

                if (!apiResponse.error && apiResponse) {
                    // Dispatch action to set initial state with fetched settings
                    dispatch(setInitialDashboardSettings(apiResponse));
                }
            } catch (error) {
                console.error('Error fetching dashboard settings:', error);
            }
        };

        if (!isMountingRef.current) {
            isMountingRef.current = true;
            fetchDashboardSettings();
        }
    }, [dispatch]);

    // Effect 2: Save dashboard settings when updateBackend flag is true
    useEffect(() => {
        // Only save if updateBackend is true and after initial mount
        console.log('updateBackend', updateBackend);
        if (!updateBackend) return;

        const saveDashboardSettings = async () => {
            try {
                const payload = {
                    selectedEquipment,
                    dateRange,
                    tabSelected,
                };

                await API.call(
                    {
                        endpoint: API.endpoints['updateDashboardSettings'],
                        method: 'post',
                        options: {
                            data: payload.toJS ? payload.toJS() : payload,
                        },
                    },
                );

                // Set updateBackend to false after successful post
                dispatch(setUpdateBackend(false));
            } catch (error) {
                console.error('Error saving dashboard settings:', error);
                // Set to false even on error to allow retry
                dispatch(setUpdateBackend(false));
            }
        };

        saveDashboardSettings();
    }, [updateBackend, selectedEquipment, dateRange, tabSelected, dispatch]);
}
